import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ExamPaperListItem, deleteExamPaper, fetchExamPapers } from '../../api/exam'
import { useAuthStore } from '@/store/authStore'
import { useLookupStore } from '@/store/lookupStore'
import { ROUTES, generatePath } from '@/constants/routes'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Share2, Pencil, Trash2, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import { copyToClipboard, cn } from '@/lib/utils'

const ALL_VALUE = ''

// 年份列表（动态生成）
const YEARS = Array.from({ length: 22 }, (_, i) => {
  const y = String(2026 - i)
  return { value: y, label: y }
})

const PAGE_SIZE = 20

interface FilterRowProps {
  label: string
  options: (string | { value: string; label: string })[]
  value: string
  onChange: (value: string) => void
}

function FilterRow({ label, options, value, onChange }: FilterRowProps) {
  return (
    <div className="flex items-start text-sm">
      <span className="w-16 shrink-0 text-muted-foreground py-1">{label}：</span>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <button
          className={cn(
            "px-2 py-1 rounded transition-colors hover:text-primary",
            value === ALL_VALUE
              ? "bg-primary text-primary-foreground font-medium shadow-sm"
              : "text-foreground hover:bg-muted"
          )}
          onClick={() => onChange(ALL_VALUE)}
        >
          全部
        </button>
        {options.map((opt) => {
          const optValue = typeof opt === 'string' ? opt : opt.value
          const optLabel = typeof opt === 'string' ? opt : opt.label
          return (
            <button
              key={optValue}
              className={cn(
                "px-2 py-1 rounded transition-colors hover:text-primary",
                value === optValue
                  ? "bg-primary text-primary-foreground font-medium shadow-sm"
                  : "text-foreground hover:bg-muted"
              )}
              onClick={() => onChange(optValue)}
            >
              {optLabel}
            </button>
          )
        })}
      </div>
    </div>
  )
}

const SELECT_ALL = '__all__'

function FilterSelect({ label, options, value, onChange }: FilterRowProps) {
  const selectedOption = options.find((opt) => {
    const optValue = typeof opt === 'string' ? opt : opt.value
    return optValue === value
  })
  const selectedLabel = selectedOption
    ? typeof selectedOption === 'string'
      ? selectedOption
      : selectedOption.label
    : undefined

  return (
    <div className="flex items-center text-sm gap-2">
      <span className="w-16 shrink-0 text-muted-foreground">{label}：</span>
      <Select value={value || SELECT_ALL} onValueChange={(v) => onChange(v === SELECT_ALL ? ALL_VALUE : v)}>
        <SelectTrigger className="w-[140px] h-8">
          <SelectValue placeholder="全部">{selectedLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SELECT_ALL}>全部</SelectItem>
          {options.map((opt) => {
            const optValue = typeof opt === 'string' ? opt : opt.value
            const optLabel = typeof opt === 'string' ? opt : opt.label
            return (
              <SelectItem key={optValue} value={optValue}>
                {optLabel}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

const STORAGE_KEY = 'exam_list_filters'

const DEFAULT_FILTERS = () => ({
  school_level: '初中' as string,
  subject: '数学',
  category: '校内',
  exam_type: ALL_VALUE,
  year: ALL_VALUE,
  grade: ALL_VALUE,
  search: ''
})

function loadFilters(): ReturnType<typeof DEFAULT_FILTERS> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) return { ...DEFAULT_FILTERS(), ...JSON.parse(raw) }
  } catch {}
  return DEFAULT_FILTERS()
}

function saveFilters(f: ReturnType<typeof DEFAULT_FILTERS>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(f))
  } catch {}
}

export default function ExamListPage() {
  const [papers, setPapers] = useState<ExamPaperListItem[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [deletingPaperId, setDeletingPaperId] = useState<string | null>(null)
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [filters, setFilters] = useState(loadFilters)
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isSuperAdmin = user?.role === 'admin'

  // Lookup store
  const {
    stages, grades, subjects, examTypes, examCategories, competitions, regions,
    fetchAll, fetchGradesByStage, fetchSubjectsByStage, fetchCompetitionsBySubject,
  } = useLookupStore()

  // 初始化加载 lookup 数据（不含 subjects，subjects 按学段单独加载）
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // 学段变化时加载年级和学科
  useEffect(() => {
    if (filters.school_level) {
      fetchGradesByStage(filters.school_level)
      fetchSubjectsByStage(filters.school_level)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.school_level])

  // 竞赛类别时加载竞赛列表
  useEffect(() => {
    if (filters.category === '竞赛' && filters.subject) {
      fetchCompetitionsBySubject(filters.subject)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.category, filters.subject])

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search)
    }, 300)
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [filters.search])

  // Persist filters to sessionStorage whenever they change
  const handleSetFilters = (updater: Parameters<typeof setFilters>[0]) => {
    setFilters(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      saveFilters(next)
      return next
    })
  }

  // Apply user profile defaults only when sessionStorage has no saved data (first visit)
  useEffect(() => {
    if (!user?.stage) return
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) return

    const matchedStage = stages.find(s => s.code === user.stage || s.name === user.stage)
    if (!matchedStage) return

    const next = {
      ...DEFAULT_FILTERS(),
      school_level: matchedStage.code,
      subject: '数学',
    }
    saveFilters(next)
    setFilters(next)
  }, [user?.stage, stages])

  // 当前学段可用的学科和年级（subjects 已按学段过滤）
  const currentSubjects = subjects.filter(s => s.type === 'regular')
  const currentGrades = grades.filter(g => g.stageCode === filters.school_level)
  const currentStageName = stages.find(s => s.code === filters.school_level)?.name || '初中'

  const loadPapers = () => {
    setLoading(true)
    setError(null)

    fetchExamPapers()
      .then(setPapers)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleSharePaper = async (paperId: string) => {
    const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}#/exam/paper/${paperId}`
    const copied = await copyToClipboard(shareUrl)
    if (copied) {
      toast.success('试卷链接已复制到剪贴板')
      return
    }
    toast.error('复制失败，请手动复制链接')
  }

  const handleDeletePaper = async (paperId: string) => {
    setDeletingPaperId(paperId)
    try {
      await deleteExamPaper(paperId)
      setPapers(prev => prev.filter(p => p.id !== paperId))
      toast.success('试卷已删除')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      toast.error(msg || '删除失败')
    } finally {
      setDeletingPaperId(prev => (prev === paperId ? null : prev))
    }
  }

  useEffect(() => {
    loadPapers()
  }, [filters])

  const displayedPapers = useMemo(() => {
    let list = [...papers]

    // Filter by category
    if (filters.category) {
      list = list.filter((p) => p.category === filters.category)
    }

    // Filter by stage + subject (直接比较，DB 和 lookup 都是中文)
    list = list.filter((p) => {
      const matchStage = !p.stage || p.stage === filters.school_level
      const matchSubject = !p.subject || p.subject === filters.subject
      return matchStage && matchSubject
    })

    // Category-specific filters
    if (filters.exam_type) {
      list = list.filter((p) =>
        p.exam_type === filters.exam_type || p.competition_code === filters.exam_type
      )
    }
    if (filters.grade) {
      list = list.filter((p) => p.grade === filters.grade)
    }

    // Common filters
    if (filters.year) {
      const year = Number.parseInt(filters.year, 10)
      if (!Number.isNaN(year)) {
        list = list.filter((p) => p.year === year)
      }
    }

    // Search by keyword (多字段模糊匹配)
    if (debouncedSearch) {
      const keyword = debouncedSearch.toLowerCase()
      list = list.filter((p) => {
        const searchableFields = [
          p.name,
          p.exam_type,
          p.source_book,
          (p.tags as Record<string, unknown>)?.source_name as string,
        ]
        return searchableFields.some(
          (field) => field && field.toLowerCase().includes(keyword)
        )
      })
    }

    // Sort by year descending
    list.sort((a, b) => (b.year || 0) - (a.year || 0))

    return list
  }, [papers, filters, debouncedSearch])

  const totalPages = Math.max(1, Math.ceil(displayedPapers.length / PAGE_SIZE))
  const paginatedPapers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return displayedPapers.slice(start, start + PAGE_SIZE)
  }, [displayedPapers, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 bg-muted/30 min-h-screen">
      {/* 筛选区域 */}
      <div className="flex items-center justify-between">
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-foreground mb-2">筛选条件：</div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* 学段下拉框 */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">学段：</span>
                <Select
                  value={filters.school_level}
                  onValueChange={(v) => {
                    handleSetFilters(prev => ({
                      ...prev,
                      school_level: v,
                      subject: '数学',
                      grade: ALL_VALUE,
                    }))
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="学段">
                      {stages.find((s) => s.code === filters.school_level)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {stages.filter(s => s.code !== '大学').map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 学科下拉框 */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">学科：</span>
                <Select
                  value={filters.subject}
                  onValueChange={(v) => handleSetFilters(prev => ({ ...prev, subject: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="学科">
                      {currentSubjects.find((s) => s.code === filters.subject)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {currentSubjects.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 年级下拉框 */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">年级：</span>
                <Select
                  value={filters.grade || SELECT_ALL}
                  onValueChange={(v) => handleSetFilters(prev => ({ ...prev, grade: v === SELECT_ALL ? ALL_VALUE : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="全部年级">
                      {currentGrades.find((g) => g.code === filters.grade)?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>全部</SelectItem>
                    {currentGrades.map(g => (
                      <SelectItem key={g.code} value={g.code}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 类别下拉框 */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">类别：</span>
                <Select
                  value={filters.category}
                  onValueChange={(v) => handleSetFilters(prev => ({
                    ...prev,
                    category: v,
                    exam_type: ALL_VALUE,
                  }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="校内">校内</SelectItem>
                    <SelectItem value="竞赛">竞赛</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 年份下拉框 */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">年份：</span>
                <Select
                  value={filters.year || SELECT_ALL}
                  onValueChange={(v) => handleSetFilters(prev => ({ ...prev, year: v === SELECT_ALL ? ALL_VALUE : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="全部年份">
                      {YEARS.find(y => y.value === filters.year)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>全部</SelectItem>
                    {YEARS.map(y => (
                      <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 类型下拉框（根据类别切换选项来源） */}
              <div className="flex items-center text-sm gap-2">
                <span className="shrink-0 text-muted-foreground">类型：</span>
                <Select
                  value={filters.exam_type || SELECT_ALL}
                  onValueChange={(v) => handleSetFilters(prev => ({ ...prev, exam_type: v === SELECT_ALL ? ALL_VALUE : v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="全部类型">
                      {(filters.category === '竞赛' ? competitions : examTypes).find(
                        t => (t.code || t) === filters.exam_type
                      )?.name}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_ALL}>全部</SelectItem>
                    {filters.category === '竞赛'
                      ? competitions.map(c => (
                          <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                        ))
                      : examTypes.map(t => (
                          <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                        ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* 搜索框 */}
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="输入关键词搜索试卷名称、来源..."
              value={filters.search}
              onChange={(e) => handleSetFilters(prev => ({ ...prev, search: e.target.value }))}
              className="pl-9 pr-9 h-10"
            />
            {filters.search && (
              <button
                onClick={() => handleSetFilters(prev => ({ ...prev, search: '' }))}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="text-center py-10 text-muted-foreground">加载中...</div>}
      {error && <div className="text-red-500 text-sm text-center py-10">{error}</div>}

      {!loading && !error && displayedPapers.length === 0 && (
        <div className="text-center py-20 text-muted-foreground bg-muted/20 rounded-xl border border-dashed">
          暂无符合条件的试卷
        </div>
      )}

      <div className="space-y-4">
        {paginatedPapers.map((paper) => {
          const canManagePaper = isSuperAdmin || (typeof paper.created_by === 'number' && paper.created_by === user?.id)

          return (
            <div
              key={paper.id}
              className="group relative bg-card border rounded-xl p-3 sm:p-5 hover:shadow-md transition-all hover:border-primary/50"
            >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
              <div className="space-y-3 flex-1 min-w-0">
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded shrink-0">
                    {currentStageName}{paper.subject || '数学'}
                  </span>
                  <Link to={generatePath(ROUTES.EXAM_PAPER, { paperId: paper.id })} className="block min-w-0 flex-1" title={paper.name}>
                    <h3 className="text-base sm:text-lg font-medium leading-snug group-hover:text-primary transition-colors line-clamp-2 break-words">
                      {paper.name}
                    </h3>
                  </Link>
                  {paper.grade && (
                    <span className="text-xs text-muted-foreground border px-1.5 rounded shrink-0">
                      {paper.grade}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <span>
                    {paper.exam_type || paper.competition_code ? (
                      <button
                        type="button"
                        className="text-primary cursor-pointer hover:underline"
                        onClick={() => handleSetFilters(prev => ({ ...prev, category: paper.category || '校内', exam_type: paper.exam_type || paper.competition_code || ALL_VALUE }))}
                      >
                        {paper.exam_type || paper.competition_code}
                      </button>
                    ) : (
                      <span>未标注</span>
                    )}
                  </span>
                  <div className="flex items-center gap-4 ml-auto sm:ml-0">
                    <span>题目：{paper.question_count ?? 0}道</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-3 shrink-0">
                <div className="text-xs text-muted-foreground">
                  <span className="hidden sm:inline">上传时间：</span>
                  {paper.year ? `${paper.year}-01-01` : '2024-01-01'}
                </div>
                <div className="flex items-center gap-1 sm:gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-muted-foreground hover:text-yellow-500 hover:bg-yellow-50 px-2"
                    onClick={(e) => {
                      e.preventDefault()
                      handleSharePaper(paper.id)
                    }}
                  >
                    <Share2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                    <span className="hidden sm:inline">分享</span>
                  </Button>
                  {canManagePaper && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-muted-foreground hover:text-foreground px-2"
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(generatePath(ROUTES.EXAM_EDITOR, { paperId: paper.id }))
                        }}
                      >
                        <Pencil className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        <span className="hidden sm:inline">编辑</span>
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 px-2"
                            disabled={deletingPaperId === paper.id}
                          >
                            <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                            <span className="hidden sm:inline">删除</span>
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>确认删除试卷？</AlertDialogTitle>
                            <AlertDialogDescription>
                              删除后将无法恢复，并会同时删除该试卷下的练习记录。
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>取消</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={async () => {
                                await handleDeletePaper(paper.id)
                              }}
                            >
                              删除
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                  <Button
                    size="sm"
                    className="h-8 px-4 bg-primary hover:bg-primary/90"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate(generatePath(ROUTES.EXAM_PAPER, { paperId: paper.id }))
                    }}
                  >
                    练习
                  </Button>
                </div>
              </div>
            </div>
            </div>
          )
        })}
      </div>
      {!loading && !error && displayedPapers.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <div className="text-sm text-muted-foreground">
            第 {currentPage} / {totalPages} 页，共 {displayedPapers.length} 条，每页 {PAGE_SIZE} 条
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
